import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {Table} from '@/components/table'
import {Text} from '@/components/text'
import type {PartnerProgrammeSliceFragment} from '@/graphql-types'
import {Section} from '@/styles/grid'
import hasNonEmptyItems from '@/utils/has-non-empty-items'
import mq from '@/utils/media-query'

interface Props {
    title: PartnerProgrammeSliceFragment['primary']['title']
    tables_tick_screen_reader_text: PartnerProgrammeSliceFragment['primary']['tables_tick_screen_reader_text']
    items: PartnerProgrammeSliceFragment['items']
}

export const PartnerProgrammeSlice = ({title, tables_tick_screen_reader_text, items}: Props) => {
    return (
        <Section>
            <Text forwardedAs="h2" $variant="heading-base">
                {title}
            </Text>

            {hasNonEmptyItems(items) &&
                items.map(({partner_programme_tables}) => {
                    const {rows, disclaimer, ...header} = partner_programme_tables.document.data
                    const headers = Object.values(header)
                    const featureName = rows.map((row) => row.feature)
                    const featureTooltips = rows.map((row) => row.feature_tooltip || '')
                    const bodyColumns = rows.map((row) => [
                        row.new_partner_text || row.new_partner,
                        row.bronze_text || row.bronze,
                        row.silver_text || row.silver,
                        row.gold_text || row.gold,
                        row.platinum_text || row.platinum,
                    ])

                    return (
                        <TableWrapper key={partner_programme_tables.document.id}>
                            <Table
                                headColumns={headers}
                                bodyHeads={featureName}
                                bodyColumns={bodyColumns}
                                bodyHeadsTooltips={featureTooltips}
                                tickAltText={tables_tick_screen_reader_text}
                                disclaimer={disclaimer}
                            />
                        </TableWrapper>
                    )
                })}
        </Section>
    )
}

const TableWrapper = styled.div`
    margin-top: ${({theme}) => theme.spacings.s60};

    ${mq.min('lg')} {
        margin-top: 80px;
    }
`

export const partnerProgrammeSliceFragment = graphql`
    fragment PartnerProgrammeSlice on PrismicSlicePageDataBodyPartnerProgramme {
        __typename
        primary {
            title
            tables_tick_screen_reader_text
        }
        items {
            partner_programme_tables {
                document {
                    ... on PrismicPartnersProgrammeTable {
                        id
                        data {
                            category
                            new_partner_heading
                            bronze_heading
                            silver_heading
                            gold_heading
                            platinum_heading
                            rows {
                                feature
                                feature_tooltip
                                new_partner
                                new_partner_text
                                bronze
                                bronze_text
                                silver
                                silver_text
                                gold
                                gold_text
                                platinum
                                platinum_text
                            }
                            disclaimer
                        }
                    }
                }
            }
        }
    }
`
